import { Component, inject, OnInit, signal, Signal } from '@angular/core'
import { DivisorUiComponent } from '@monorepo-channels/components/ui-common'
import { CommonModule } from '@angular/common'
import { FooterButtonsComponent, LoadingPageComponent } from '@monorepo-channels/components/ui-pm'
import { MatButtonModule } from '@angular/material/button'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import { distinctUntilChanged, filter, Subject, switchMap, take, takeUntil } from 'rxjs'
import { CepService, isMobile, openResponsiveComponent } from '@monorepo-channels/shared/util-helpers'
import { getClient } from '@monorepo-channels/channels/domain'
import { Basket, BasketService } from '../basket.service'
import { MatIconModule } from '@angular/material/icon'
import { RouterLink } from '@angular/router'
import { GenericErrorComponent } from '@monorepo-channels/feature-pm'
import { MatDialog } from '@angular/material/dialog'
import { MatBottomSheet } from '@angular/material/bottom-sheet'

interface FieldState {
	focused: Signal<boolean>
	editing: Signal<boolean>
	setFocused: (value: boolean) => void
	setEditing: (value: boolean) => void
}

@Component({
	standalone: true,
	selector: 'pm-select-basket-page',
	imports: [
		DivisorUiComponent,
		CommonModule,
		FooterButtonsComponent,
		MatButtonModule,
		ReactiveFormsModule,
		MatIconModule,
		RouterLink,
		LoadingPageComponent,
	],
	styleUrls: ['./select-basket.component.scss'],
	templateUrl: './select-basket.component.html',
})
export class SelectBasketPageComponent implements OnInit {
	private basketService = inject(BasketService)
	private fb = inject(FormBuilder)
	private store = inject(Store)
	private cepService = inject(CepService)
	private destroy$ = new Subject<void>()
	private dialog = inject(MatDialog)
	private bottomSheet = inject(MatBottomSheet)

	fieldStates = new Map<string, FieldState>()

	baskets: Basket[] = []
	selectedBasketId: string | null = null
	isFormVisible = false
	isOrderSuccess = false
	isLoading = false
	isCepLoading = false
	isMobile = isMobile()
	expandedBasketIds = new Set<string>()

	venueId = '638a384316a302004ae88848'

	userForm = this.fb.nonNullable.group({
		name: [{ value: '', disabled: true }],
		email: [{ value: '', disabled: true }],
		phone: [{ value: '', disabled: true }],
		cpf: [{ value: '', disabled: true }],
		state: [{ value: '', disabled: false }, Validators.required],
		city: [{ value: '', disabled: false }, Validators.required],
		zipCode: [{ value: '', disabled: false }, Validators.required],
		street: [{ value: '', disabled: false }, Validators.required],
		number: [{ value: '', disabled: false }, Validators.required],
		neighborhood: [{ value: '', disabled: false }, Validators.required],
		complement: [{ value: '', disabled: false }],
		acceptedTerms: [false, Validators.requiredTrue],
	})

	constructor() {
		;[
			'name',
			'email',
			'phone',
			'cpf',
			'state',
			'city',
			'zipCode',
			'street',
			'number',
			'neighborhood',
			'complement',
		].forEach(fieldName => this.initializeFieldState(fieldName))
	}

	private initializeFieldState(fieldName: string) {
		const focused = signal(false)
		const editing = signal(false)

		this.fieldStates.set(fieldName, {
			focused,
			editing,
			setFocused: (value: boolean) => focused.set(value),
			setEditing: (value: boolean) => editing.set(value),
		})
	}

	ngOnInit(): void {
		this.isLoading = true
		this.basketService.getAll().subscribe({
			next: baskets => {
				this.baskets = baskets
				this.isLoading = false
			},
			error: error => {
				openResponsiveComponent({
					component: GenericErrorComponent,
					isMobile: this.isMobile,
					bottomSheet: this.bottomSheet,
					dialog: this.dialog,
					config: {
						data: {
							error: error.error.message,
							title: 'Erro ao buscar Cestas',
							btnMessage: 'Fechar',
						},
					},
				})
				this.isLoading = false
			},
		})

		this.store
			.select(getClient)
			.pipe(take(1))
			.subscribe(client => {
				if (client) {
					this.userForm.patchValue({
						name: `${client.fullName}`,
						email: client.email,
						phone: client.phone ?? '',
						cpf: client.cpf,
					})
				}
			})

		this.userForm.controls.zipCode.valueChanges
			.pipe(
				filter(zip => zip?.length >= 8),
				distinctUntilChanged(),
				switchMap(zip => {
					this.isCepLoading = true
					return this.cepService.findCep(zip)
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(result => {
				this.isCepLoading = false
				if (result.error) {
					openResponsiveComponent({
						component: GenericErrorComponent,
						isMobile: this.isMobile,
						bottomSheet: this.bottomSheet,
						dialog: this.dialog,
						config: {
							data: {
								error: 'CEP não encontrado',
								title: 'Erro ao buscar CEP',
								btnMessage: 'Fechar',
							},
						},
					})
					return
				}
				this.userForm.patchValue({
					state: result.state,
					city: result.city,
					street: result.address,
					neighborhood: result.neighborhood,
				})
			})
	}

	getFieldState(fieldName: string): FieldState {
		return this.fieldStates.get(fieldName)!
	}

	onSelectBasket(selectedBasketId: string) {
		this.selectedBasketId = selectedBasketId
	}

	requestBasket() {
		if (this.userForm.invalid) {
			this.userForm.markAllAsTouched()
			return
		}

		const formData = this.userForm.getRawValue()
		this.isLoading = true

		this.store
			.select(getClient)
			.pipe(take(1))
			.subscribe(client => {
				if (!client?.id || !client.voucher?.id) {
					this.isLoading = false
					return
				}

				const payload = {
					clientId: client.id,
					venueId: this.venueId,
					voucherId: client!.voucher!.id,
					basketId: this.selectedBasketId || '',
					deliveryData: {
						address: formData.street,
						zipCode: formData.zipCode,
						city: formData.city,
						state: formData.state,
						neighborhood: formData.neighborhood,
						number: formData.number,
						complement: formData.complement,
					},
				}

				this.basketService.createBasketOrder(payload).subscribe({
					next: () => {
						this.isOrderSuccess = true
						this.isFormVisible = false
						this.isLoading = false
					},
					error: error => {
						openResponsiveComponent({
							component: GenericErrorComponent,
							isMobile: this.isMobile,
							bottomSheet: this.bottomSheet,
							dialog: this.dialog,
							config: {
								data: {
									error: error.error.message,
									title: 'Erro ao Realizar solicitação',
									btnMessage: 'Fechar',
								},
							},
						})
						this.isLoading = false
					},
				})
			})
	}

	toggleDescription(event: MouseEvent, basketId: string) {
		event.stopPropagation()
		event.preventDefault()
		if (this.expandedBasketIds.has(basketId)) {
			this.expandedBasketIds.delete(basketId)
		} else {
			this.expandedBasketIds.add(basketId)
		}
	}
}
